import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  FormControlLabel,
  Paper,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  CloudUpload as UploadIcon,
  CheckCircle as CheckIcon,
  Error as ErrorIcon,
  Refresh as RefreshIcon,
  ArrowBack as BackIcon,
} from '@mui/icons-material';
import { Link as RouterLink } from 'react-router-dom';
import api from '../utils/api';

interface RestoreDocument {
  id: number;
  title: string;
  version: string;
  is_current_version: boolean;
  is_active: boolean;
  storage_path: string;
  file_exists: boolean;
  file_size: number | null;
  file_size_display: string | null;
  created_at: string;
}

interface RestoreSummary {
  total_with_paths: number;
  listed: number;
  missing: number;
  present: number;
}

const DocumentTemporaryRestore: React.FC = () => {
  const [documents, setDocuments] = useState<RestoreDocument[]>([]);
  const [summary, setSummary] = useState<RestoreSummary | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [missingOnly, setMissingOnly] = useState(true);
  const [includeInactive, setIncludeInactive] = useState(false);
  const [uploadingId, setUploadingId] = useState<number | null>(null);
  const fileInputRefs = useRef<Record<number, HTMLInputElement | null>>({});

  const fetchStatus = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const params = new URLSearchParams();
      if (missingOnly) params.append('missing_only', 'true');
      if (includeInactive) params.append('include_inactive', 'true');
      const response = await api.get(`/documents/restore-status/?${params.toString()}`);
      setDocuments(response.data.documents || []);
      setSummary(response.data.summary || null);
    } catch (err: any) {
      setError(err.response?.data?.error || err.response?.data?.detail || 'Failed to load document restore status');
    } finally {
      setLoading(false);
    }
  }, [missingOnly, includeInactive]);

  useEffect(() => {
    fetchStatus();
  }, [fetchStatus]);

  const handleUpload = async (documentId: number, file: File) => {
    if (!file.name.toLowerCase().endsWith('.pdf')) {
      setError('Only PDF files are allowed');
      return;
    }
    if (file.size > 25 * 1024 * 1024) {
      setError('File size cannot exceed 25MB');
      return;
    }

    try {
      setUploadingId(documentId);
      setError(null);
      setSuccess(null);

      const formData = new FormData();
      formData.append('file', file);
      await api.post(`/documents/${documentId}/restore-file/`, formData);

      setSuccess(`Restored file for document #${documentId}`);
      await fetchStatus();
    } catch (err: any) {
      const data = err.response?.data;
      setError(data?.error || data?.file?.[0] || data?.detail || 'Upload failed');
    } finally {
      setUploadingId(null);
    }
  };

  const triggerFileSelect = (documentId: number) => {
    fileInputRefs.current[documentId]?.click();
  };

  return (
    <Box sx={{ p: 3 }}>
      <Box display="flex" alignItems="center" gap={2} mb={2}>
        <Button component={RouterLink} to="/documents" startIcon={<BackIcon />} variant="outlined">
          Back to Documents
        </Button>
        <Typography variant="h4" component="h1">
          Document File Restore
        </Typography>
      </Box>

      <Alert severity="warning" sx={{ mb: 3 }}>
        Temporary admin tool: upload PDFs for existing database records. Files are saved to each
        document&apos;s existing storage path on the Railway volume. Remove this page after restore
        is complete.
      </Alert>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}
      {success && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>
          {success}
        </Alert>
      )}

      <Paper sx={{ p: 2, mb: 3 }}>
        <Box display="flex" flexWrap="wrap" alignItems="center" gap={3}>
          <FormControlLabel
            control={<Switch checked={missingOnly} onChange={(e) => setMissingOnly(e.target.checked)} />}
            label="Show missing files only"
          />
          <FormControlLabel
            control={
              <Switch checked={includeInactive} onChange={(e) => setIncludeInactive(e.target.checked)} />
            }
            label="Include inactive documents"
          />
          <Button startIcon={<RefreshIcon />} onClick={fetchStatus} disabled={loading}>
            Refresh
          </Button>
          {summary && (
            <Box display="flex" gap={1}>
              <Chip label={`Missing: ${summary.missing}`} color="error" size="small" />
              <Chip label={`Present: ${summary.present}`} color="success" size="small" />
              <Chip label={`Listed: ${summary.listed}`} size="small" />
            </Box>
          )}
        </Box>
      </Paper>

      {loading ? (
        <Box display="flex" justifyContent="center" py={6}>
          <CircularProgress />
        </Box>
      ) : (
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Status</TableCell>
                <TableCell>Document</TableCell>
                <TableCell>Version</TableCell>
                <TableCell>Storage path</TableCell>
                <TableCell align="right">Upload</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {documents.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={5} align="center">
                    <Typography color="text.secondary" py={3}>
                      {missingOnly ? 'No missing documents found.' : 'No documents found.'}
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                documents.map((doc) => (
                  <TableRow key={doc.id}>
                    <TableCell>
                      {doc.file_exists ? (
                        <Tooltip title="File exists on server">
                          <CheckIcon color="success" />
                        </Tooltip>
                      ) : (
                        <Tooltip title="File missing on server">
                          <ErrorIcon color="error" />
                        </Tooltip>
                      )}
                    </TableCell>
                    <TableCell>
                      <Typography variant="subtitle2">{doc.title}</Typography>
                      <Typography variant="caption" color="text.secondary">
                        ID {doc.id}
                        {!doc.is_active && ' • Inactive'}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={doc.version}
                        size="small"
                        color={doc.is_current_version ? 'success' : 'default'}
                      />
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" sx={{ fontFamily: 'monospace', wordBreak: 'break-all' }}>
                        {doc.storage_path}
                      </Typography>
                    </TableCell>
                    <TableCell align="right">
                      <input
                        type="file"
                        accept=".pdf,application/pdf"
                        style={{ display: 'none' }}
                        ref={(el) => {
                          fileInputRefs.current[doc.id] = el;
                        }}
                        onChange={(e) => {
                          const file = e.target.files?.[0];
                          if (file) {
                            handleUpload(doc.id, file);
                          }
                          e.target.value = '';
                        }}
                      />
                      <Button
                        variant={doc.file_exists ? 'outlined' : 'contained'}
                        size="small"
                        startIcon={
                          uploadingId === doc.id ? <CircularProgress size={16} /> : <UploadIcon />
                        }
                        disabled={uploadingId === doc.id}
                        onClick={() => triggerFileSelect(doc.id)}
                      >
                        {doc.file_exists ? 'Replace' : 'Upload PDF'}
                      </Button>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Box>
  );
};

export default DocumentTemporaryRestore;
